// FileExplorer — die Windows-Explorer-Ansicht für Dateien.
//
// Datenquellen-agnostisch und vollständig KONTROLLIERT: kein Fetch, kein
// react-router, keine Service-Importe. Gefüttert wird sie von zwei Adaptern
// (Document-API bzw. Kundenakten-Index). Fehlt ein Aktions-Callback,
// verschwindet die Aktion — die read-only-Variante ist damit derselbe Code
// ohne Sonderfall-Flag.
//
// EINBETTUNG: Die Komponente bringt ihren EIGENEN `.kvp-rd`-Root mit und
// importiert das Stylesheet selbst. Drei ihrer vier Einsatzorte
// (Legacy-ContactDetails, Schadenmappe, AlteKundenaktenDetail) haben KEINEN
// kvp-rd-Vorfahren — ohne eigenen Root käme dort eine ungestylte HTML-Tabelle
// heraus. `docx-root` nimmt dafür `min-height:100%` und den Hintergrund des
// Seiten-Roots zurück (siehe contactRedesign.css).

import * as React from "react";
import { DataTable, type Column, RdContextMenu, type RdMenuItem } from "../../redesign/table";
import { Checkbox } from "../../redesign/table/cells";
import "../../redesign/contactRedesign.css";
import { humanSize } from "../../../utils/fileUtils";
import { renderSnippet } from "../../../utils/renderSnippet";
import { ExplorerBreadcrumb } from "./ExplorerBreadcrumb";
import { FolderTree } from "./FolderTree";
import { FileIcon, FolderIcon, fileTypeLabel } from "./fileTypeIcons";
import { useExplorerSelection } from "./useExplorerSelection";
import { dateienAusDrop, ziehtDateien, type AbgelegteDatei } from "./dateiDrop";
import {
  compareFolders,
  liegtIn,
  parentPath,
  rowKeyOf,
  type ExplorerFile,
  type ExplorerFolder,
  type ExplorerGroup,
  type ExplorerRow,
} from "./explorerModel";
import { alleFenster } from "../../windowManager/browserFenster/fensterRegister";

/** Unterhalb dieser GEMESSENEN Panelbreite wird der Ordnerbaum ausgeblendet.
 *  Bewusst an der Panelbreite und nicht am Viewport: die App-Sidebar frisst
 *  ~250px, eine Viewport-Media-Query träfe daneben. */
const NARROW_PX = 720;
/** Ab so vielen markierten Dateien wird vor dem Sammel-Download rückgefragt
 *  (Browser blocken sequenzielle Downloads etwa ab dieser Größenordnung). */
export const BULK_CONFIRM_THRESHOLD = 5;

const fmtDate = (s: string | null | undefined): string => {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("de-DE");
};

export type FileExplorerProps<P = unknown> = {
  /** Beschriftung der Wurzel im Baum und Breadcrumb. */
  rootLabel: string;
  /** Aktueller Ordnerpfad ("" = Wurzel, sonst auf "/" endend). */
  path: string;
  onNavigate: (path: string) => void;

  /** Unterordner der aktuellen Ebene (Liste rechts). */
  folders: ExplorerFolder[];
  /** Dateien der aktuellen Ebene bzw. Suchtreffer. */
  files: ExplorerFile<P>[];
  /** Virtuelle Dateigruppen der aktuellen Ebene (z. B. „Vorgänge"). */
  groups?: ExplorerGroup<P>[];

  /** Ordner-Browsing (Baum + Breadcrumb) oder flache Liste/Suche. */
  browsing: boolean;

  /** Baum: Kinder einer Ebene; `undefined` = noch nicht geladen. */
  treeChildren?: (path: string) => ExplorerFolder[] | undefined;
  expandedPaths?: ReadonlySet<string>;
  onToggleExpand?: (path: string) => void;
  /** Breite des Ordnerbaums in px (kontrolliert; der Adapter persistiert sie). */
  treeWidth?: number;
  onTreeWidthChange?: (px: number) => void;

  loading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** Eindeutige Id des Scroll-Containers (Sticky-Header + InfiniteScroll). */
  scrollContainerId: string;

  /** Kopfleiste (Suche, Modus-Umschalter, Upload) — kommt von der Shell. */
  toolbar?: React.ReactNode;
  emptyText?: React.ReactNode;

  /** Kategorie-Spalte zeigen (flache Liste über alle Dokumente). */
  showCategory?: boolean;

  // --- Aktionen. Fehlt ein Callback, verschwindet die Möglichkeit. ---
  onOpenFile?: (file: ExplorerFile<P>) => void;
  onDownload?: (file: ExplorerFile<P>) => void;
  onRename?: (file: ExplorerFile<P>) => void;
  /** Kategorie nachträglich zuordnen/ändern (`Document.category`). */
  onSetCategory?: (file: ExplorerFile<P>) => void;
  /** Sammel-Download; erhält die markierten Dateien. */
  onBulkDownload?: (files: ExplorerFile<P>[]) => void;
  /**
   * Endgültiges Löschen. Fehlt der Callback, steht „Löschen" nicht im Menü.
   * Die Rückfrage gehört dem Aufrufer — nur er kennt den Kontext (Kundenakte,
   * Risiko, Termin) und weiß, was danach neu zu laden ist.
   */
  onDelete?: (file: ExplorerFile<P>) => void;
  /**
   * Sammel-Löschen; erhält ALLE markierten löschbaren Zeilen — Dateien und
   * Ordner (User 22.10.2026: „mehrere ausgewählt → Löschen → nur eines weg").
   * Ordner nur, soweit `canDeleteFolder` sie freigibt. Die Rückfrage gehört
   * wie bei `onDelete` dem Aufrufer.
   */
  onBulkDelete?: (auswahl: ExplorerAuswahl<P>) => void;
  /** Darf dieser Ordner mitgelöscht werden? Ohne Angabe: nein. */
  canDeleteFolder?: (folder: ExplorerFolder) => boolean;
  /**
   * Aus dem Papierkorb zurückholen (PO 16.09.2026). Fehlt der Callback, steht
   * „Wiederherstellen" nicht im Menü — im Normalbetrieb (nur aktive Dateien)
   * gibt der Aufrufer ihn gar nicht erst mit. Der Eintrag erscheint zudem nur
   * an Zeilen, die `deleted` gesetzt haben.
   */
  onRestore?: (file: ExplorerFile<P>) => void;
  /** Klick auf eine nicht öffenbare Datei (read-only) — z. B. für einen Hinweis. */
  onInertFileClick?: (file: ExplorerFile<P>) => void;
  /**
   * Klick auf die Kennzeichnung hinter dem Namen (`ExplorerFile.tag`). Opt-in:
   * ohne Callback bleibt das Tag der bisherige graue Text (Alte Kundenakten
   * zeigen so „Schaden"); mit Callback wird es ein Chip, der NICHT die Zeile
   * öffnet — die Kundenakte springt damit in den Herkunfts-Vorgang (P6).
   */
  onTagClick?: (file: ExplorerFile<P>) => void;

  /**
   * Anzeigename eines Pfades in den Brotkrumen (`"A/B/"` → „B"). Für Pfade,
   * deren letztes Segment kein lesbarer Name ist (KMVS-Teilbaum: Vorgangs-Id
   * statt Betreff). `undefined` = das Segment selbst.
   */
  segmentLabel?: (path: string) => string | undefined;
  /** Kontextmenü eines Ordners. Fehlt es oder ist es leer, gibt es keins. */
  folderMenuItems?: (folder: ExplorerFolder) => RdMenuItem[];
  /**
   * Kontextmenü auf der FREIEN Fläche der Liste (PO 28.09.2026: „ins Freie
   * rechtsklicken" → Neuer Ordner, Neue Datei …). Leer = kein Menü.
   */
  backgroundMenuItems?: (path: string) => RdMenuItem[];
  /** Zusatz unter der Liste (z. B. „Alte Kundenakte hier zuweisen"). */
  belowList?: React.ReactNode;

  /**
   * Verschieben per Drag & Drop (PO 28.09.2026). Gezogen wird die Auswahl,
   * wenn die gegriffene Zeile dazugehört, sonst nur die Zeile. Ziele sind
   * Ordnerzeilen, Knoten im Baum und die Brotkrumen. Fehlt der Callback, ist
   * nichts ziehbar.
   */
  onMove?: (drag: { files: ExplorerFile<P>[]; folders: ExplorerFolder[] }, targetPath: string) => void;
  /**
   * „Verschieben nach…" im Rechtsklick-Menü einer Datei — der zweite Weg neben
   * Drag & Drop (PO 06.10.2026). Erhält die markierten Dateien, die sich
   * verschieben lassen (`canDrag`); der Aufrufer fragt das Ziel ab.
   */
  onMoveRequest?: (files: ExplorerFile<P>[]) => void;
  /** Darf diese Zeile gegriffen werden? Ohne Angabe: ja. */
  canDrag?: (row: ExplorerFile<P> | ExplorerFolder) => boolean;
  /** Nimmt dieser Ordner etwas an? Ohne Angabe: ja. */
  canDropInto?: (path: string) => boolean;

  /**
   * Hinausziehen, z. B. ins Mailfenster (User 22.10.2026). Gezogen wird wie
   * beim Verschieben die Auswahl, wenn die gegriffene Zeile dazugehört. Der
   * Aufrufer schreibt seine Nutzlast in den `DataTransfer` und meldet, ob er
   * etwas gesetzt hat. Unabhängig von `onMove`: beides darf zugleich gelten,
   * das Ziel entscheidet (Ordnerzeile = verschieben, Mailfenster = anhängen).
   */
  onDragOut?: (drag: ExplorerAuswahl<P>, dataTransfer: DataTransfer) => boolean;
  /** Darf diese Datei hinausgezogen werden? Ohne Angabe: ja. */
  canDragOut?: (file: ExplorerFile<P>) => boolean;

  /**
   * Dateien vom Rechner (Windows-Explorer, Finder) auf die Liste ziehen —
   * Ziel ist der geöffnete Ordner (User 22.10.2026). Ganze Ordner kommen
   * rekursiv mit ihrem Pfad. Fehlt der Callback, nimmt die Liste nichts an.
   */
  onDropFiles?: (dateien: AbgelegteDatei[], zielPfad: string) => void;
  /** Text der Ablagezone, z. B. „Dateien nach „Verträge“ hochladen". */
  dropFilesHinweis?: string;
  /**
   * Weitere Züge, die die Liste annimmt — z. B. Mails und Mail-Anhänge aus
   * dem Postfach, auch aus einem anderen Browserfenster (User 01.11.2026).
   * Liefert für die beim Ziehen sichtbaren Typen den Text der Ablagezone,
   * `null` heißt: nicht annehmen. Der Drop geht an {@link onDropFremd}, das
   * den `DataTransfer` SYNCHRON lesen muss.
   */
  fremdAblageHinweis?: (typen: readonly string[]) => string | null;
  onDropFremd?: (dataTransfer: DataTransfer) => void;
  /**
   * Zeilen über der Liste, im Tabellenkörper — die sofort sichtbaren
   * Platzhalter einer laufenden Ablage (User 01.11.2026).
   */
  kopfZeilen?: (spalten: number) => React.ReactNode;
};

/** Markierte Zeilen, aufgeteilt nach Art. */
export type ExplorerAuswahl<P = unknown> = { files: ExplorerFile<P>[]; folders: ExplorerFolder[] };

/** Eigener MIME-Typ des Zugs — ein Datei-Drop aus Windows trägt ihn nicht. */
const DRAG_TYP = "application/x-kmvs-akte";

export function FileExplorer<P = unknown>({
  rootLabel,
  path,
  onNavigate,
  folders,
  files,
  groups = [],
  browsing,
  treeChildren,
  expandedPaths,
  onToggleExpand,
  treeWidth = 240,
  onTreeWidthChange,
  loading,
  hasMore,
  onLoadMore,
  scrollContainerId,
  toolbar,
  emptyText,
  showCategory,
  onOpenFile,
  onDownload,
  onRename,
  onSetCategory,
  onBulkDownload,
  onDelete,
  onBulkDelete,
  onRestore,
  onInertFileClick,
  onTagClick,
  segmentLabel,
  folderMenuItems,
  backgroundMenuItems,
  belowList,
  onMove,
  onMoveRequest,
  canDrag,
  canDropInto,
  canDeleteFolder,
  onDragOut,
  canDragOut,
  onDropFiles,
  dropFilesHinweis,
  fremdAblageHinweis,
  onDropFremd,
  kopfZeilen,
}: FileExplorerProps<P>) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [narrow, setNarrow] = React.useState(false);
  const [menu, setMenu] = React.useState<{ x: number; y: number; file: ExplorerFile<P> } | null>(null);
  // Ordner- und Hintergrundmenü: fertige Einträge, weil sie vom Adapter kommen.
  const [fremdMenu, setFremdMenu] = React.useState<{ x: number; y: number; title?: string; items: RdMenuItem[] } | null>(null);
  // Was gerade gezogen wird. Ein Ref statt `dataTransfer`: Quelle und Ziel
  // liegen im selben Explorer, und `getData` ist während `dragover` gesperrt.
  const zug = React.useRef<{ files: ExplorerFile<P>[]; folders: ExplorerFolder[] } | null>(null);
  React.useEffect(() => {
    const ende = () => {
      // Nach dem Loslassen (oder Abbrechen) ist der Zug vorbei — sonst nähme
      // eine Ordnerzeile später einen Datei-Drop aus Windows als Verschieben.
      window.setTimeout(() => {
        zug.current = null;
      }, 0);
    };
    alleFenster.addEventListener("dragend", ende);
    alleFenster.addEventListener("drop", ende);
    return () => {
      alleFenster.removeEventListener("dragend", ende);
      alleFenster.removeEventListener("drop", ende);
    };
  }, []);

  // Virtuelle Gruppen zuerst, dann Ordner und Dateien — jeweils numerisch-
  // alphabetisch. Die
  // Server-Sortierung (ORDER BY fileName) kann das nicht leisten; sie ordnet
  // die geladene Seite vor, hier wird sie in Explorer-Reihenfolge gebracht.
  const rows: ExplorerRow<P>[] = React.useMemo(() => {
    const f = browsing ? [...folders].sort(compareFolders) : [];
    return [...groups, ...f, ...files];
  }, [folders, files, groups, browsing]);

  const selectableRows = React.useMemo(
    () => [...groups.flatMap((group) => group.files), ...(browsing ? folders : []), ...files],
    [groups, folders, files, browsing],
  );
  const orderedKeys = React.useMemo(() => selectableRows.map(rowKeyOf), [selectableRows]);
  const expandedGroupKeys = React.useMemo(() => groups.map(rowKeyOf), [groups]);
  const sel = useExplorerSelection(orderedKeys);

  // Auswahl beim Ordner-/Modus-Wechsel auf das Sichtbare eindampfen, damit
  // Sammel-Aktionen nie Dateien erwischen, die man gar nicht mehr sieht.
  const { retainOnly } = sel;
  React.useEffect(() => {
    retainOnly(orderedKeys);
  }, [path, browsing, orderedKeys, retainOnly]);

  // Schmales Panel an der GEMESSENEN Breite (ResizeObserver), nicht per
  // Viewport-Media-Query.
  React.useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setNarrow(e.contentRect.width < NARROW_PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const selectedFiles = React.useMemo(
    () =>
      [...groups.flatMap((group) => group.files), ...files].filter((f) =>
        sel.isSelected(rowKeyOf(f)),
      ),
    [groups, files, sel],
  );

  const selectedFolders = React.useMemo(
    () => (browsing ? folders.filter((f) => sel.isSelected(rowKeyOf(f))) : []),
    [browsing, folders, sel],
  );

  /** Was von der Auswahl gelöscht werden darf — bereits Gelöschtes nicht noch einmal. */
  const loeschbareAuswahl: ExplorerAuswahl<P> = React.useMemo(
    () => ({
      files: onDelete || onBulkDelete ? selectedFiles.filter((f) => !f.deleted) : [],
      folders: onBulkDelete && canDeleteFolder ? selectedFolders.filter(canDeleteFolder) : [],
    }),
    [selectedFiles, selectedFolders, onDelete, onBulkDelete, canDeleteFolder],
  );
  const loeschbarAnzahl = loeschbareAuswahl.files.length + loeschbareAuswahl.folders.length;

  /**
   * Eine Datei allein geht über `onDelete` (namentliche Rückfrage), alles
   * andere gesammelt über `onBulkDelete` — mit EINER Rückfrage.
   */
  const auswahlLoeschen = (a: ExplorerAuswahl<P>) => {
    if (a.folders.length === 0 && a.files.length === 1 && onDelete) onDelete(a.files[0]);
    else if (a.files.length + a.folders.length > 0) onBulkDelete?.(a);
  };

  const loeschLabel = (a: ExplorerAuswahl<P>) =>
    a.folders.length > 0
      ? `${a.files.length + a.folders.length} Elemente löschen`
      : `${a.files.length} Dateien löschen`;

  const openFolder = (f: ExplorerFolder) => onNavigate(f.path);

  const activateRow = (row: ExplorerRow<P>) => {
    if (row.kind === "group") return;
    if (row.kind === "folder") {
      openFolder(row);
      return;
    }
    if (onOpenFile) onOpenFile(row);
    else onInertFileClick?.(row);
  };

  const handleRowClick = (row: ExplorerRow<P>, _i: number, e: React.MouseEvent) => {
    if (row.kind === "group") return;
    const shouldOpen = sel.handleRowClick(rowKeyOf(row), e);
    if (shouldOpen) activateRow(row);
  };

  const handleRowContextMenu = (row: ExplorerRow<P>, _i: number, e: React.MouseEvent) => {
    e.preventDefault();
    if (row.kind === "group") {
      setMenu(null);
      return;
    }
    sel.handleContextMenu(rowKeyOf(row));
    if (row.kind === "folder") {
      setMenu(null);
      // Teil einer Mehrfachauswahl: das Ordnermenü gälte nur diesem einen
      // Ordner — „Löschen" dort ließ die übrigen markierten Zeilen stehen.
      // Stattdessen das Sammelmenü für alles Markierte.
      const mehrfach = sel.isSelected(rowKeyOf(row)) && sel.selected.size > 1;
      if (mehrfach && onBulkDelete && loeschbarAnzahl > 0) {
        setFremdMenu({
          x: e.clientX,
          y: e.clientY,
          title: `${sel.selected.size} Elemente markiert`,
          items: [
            {
              id: "delete",
              label: loeschLabel(loeschbareAuswahl),
              icon: "🗑",
              gefahr: true,
              onSelect: () => auswahlLoeschen(loeschbareAuswahl),
            },
          ],
        });
        return;
      }
      const items = folderMenuItems?.(row) ?? [];
      setFremdMenu(items.length ? { x: e.clientX, y: e.clientY, title: row.name, items } : null);
      return;
    }
    setFremdMenu(null);
    setMenu({ x: e.clientX, y: e.clientY, file: row });
  };

  /** Rechtsklick auf die freie Fläche — eine Zeile hat ihn dann nicht schon genommen. */
  const onBackgroundContextMenu = (e: React.MouseEvent) => {
    if (e.isDefaultPrevented() || !backgroundMenuItems) return;
    const t = e.target as HTMLElement;
    // Kopfzeile und Eingabefelder behalten ihr eigenes Verhalten.
    if (t.closest("thead,input,textarea,select")) return;
    const items = backgroundMenuItems(path);
    if (!items.length) return;
    e.preventDefault();
    setMenu(null);
    sel.clear();
    setFremdMenu({ x: e.clientX, y: e.clientY, items });
  };

  /* ---------------- Drag & Drop ---------------- */

  const ziehbar = (row: ExplorerRow<P>): row is ExplorerFile<P> | ExplorerFolder =>
    !!onMove && row.kind !== "group" && (canDrag ? canDrag(row) : true);
  /** Darf die Zeile hinaus (ins Mailfenster)? Nur Dateien. */
  const ausziehbar = (row: ExplorerRow<P>): row is ExplorerFile<P> =>
    !!onDragOut && row.kind === "file" && (canDragOut ? canDragOut(row) : true);
  const greifbar = (row: ExplorerRow<P>) => ziehbar(row) || ausziehbar(row);

  /** Nimmt `pfad` den laufenden Zug an? Nie in sich selbst oder einen Unterordner. */
  const nimmtAn = (pfad: string): boolean => {
    const z = zug.current;
    if (!z || !onMove) return false;
    if (canDropInto && !canDropInto(pfad)) return false;
    if (z.folders.some((f) => liegtIn(pfad, f.path))) return false;
    // Nur Dateien, die schon dort liegen → nichts zu tun.
    const ziel = pfad.replace(/\/$/, "");
    if (!z.folders.length && z.files.every((f) => f.dir === ziel)) return false;
    return true;
  };

  /** Nutzlast fürs Hinausziehen setzen; true, wenn der Aufrufer etwas gesetzt hat. */
  const hinausSetzen = (zeilen: ExplorerRow<P>[], dt: DataTransfer): boolean => {
    if (!onDragOut) return false;
    return onDragOut(
      {
        files: zeilen.filter((r): r is ExplorerFile<P> => r.kind === "file" && ausziehbar(r)),
        folders: zeilen.filter((r): r is ExplorerFolder => r.kind === "folder"),
      },
      dt,
    );
  };

  const zugStarten = (row: ExplorerRow<P>, e: React.DragEvent) => {
    if (!greifbar(row)) {
      e.preventDefault();
      return;
    }
    const gewaehlt = sel.isSelected(rowKeyOf(row));
    const markiert: ExplorerRow<P>[] = gewaehlt
      ? selectableRows.filter((r) => sel.isSelected(rowKeyOf(r)))
      : [row];
    // Verschieben nur, wenn die GEGRIFFENE Zeile selbst verschiebbar ist —
    // sonst wanderten beim Ablegen auf einem Ordner nur die übrigen.
    let intern = false;
    if (ziehbar(row)) {
      const zeilen = markiert.filter(ziehbar);
      zug.current = {
        files: zeilen.filter((r): r is ExplorerFile<P> => r.kind === "file"),
        folders: zeilen.filter((r): r is ExplorerFolder => r.kind === "folder"),
      };
      // Firefox startet den Zug nur mit irgendwelchen Daten.
      e.dataTransfer.setData(DRAG_TYP, String(zeilen.length));
      intern = true;
    } else {
      zug.current = null;
    }
    const hinaus = hinausSetzen(markiert, e.dataTransfer);
    if (!intern && !hinaus) {
      e.preventDefault();
      return;
    }
    // Beides erlaubt: die Ordnerzeile verlangt „move", das Mailfenster „copy".
    e.dataTransfer.effectAllowed = intern && hinaus ? "copyMove" : intern ? "move" : "copy";
  };

  /** Ordner aus dem Baum greifen — gezogen wird nur dieser eine. */
  const baumZugStarten = (f: ExplorerFolder, e: React.DragEvent) => {
    zug.current = { files: [], folders: [f] };
    e.dataTransfer.setData(DRAG_TYP, "1");
    // Auch ein Ordner meldet sich beim Mailfenster — das lehnt ihn mit
    // Hinweis ab, statt den Zug kommentarlos zu verweigern.
    const hinaus = hinausSetzen([f], e.dataTransfer);
    e.dataTransfer.effectAllowed = hinaus ? "copyMove" : "move";
  };

  /**
   * Rechtsklick im Baum: ein Ordner bekommt sein Ordnermenü, die Wurzel das
   * Menü der freien Fläche — bezogen auf die Wurzel, nicht auf den gerade
   * geöffneten Ordner.
   */
  const baumKontextmenue = (f: ExplorerFolder | null, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu(null);
    const items = f ? folderMenuItems?.(f) ?? [] : backgroundMenuItems?.("") ?? [];
    setFremdMenu(items.length ? { x: e.clientX, y: e.clientY, title: f ? f.name : rootLabel, items } : null);
  };

  const fallenLassen = (pfad: string) => {
    const z = zug.current;
    zug.current = null;
    if (z && onMove) onMove(z, pfad);
  };

  /* ---------- Dateien vom Rechner ablegen ---------- */

  const [dateiAblage, setDateiAblage] = React.useState(false);
  /** Text der Ablagezone, solange ein fremder Zug (Postfach) darüber steht. */
  const [fremdHinweis, setFremdHinweis] = React.useState<string | null>(null);
  const fremdText = (e: React.DragEvent): string | null =>
    onDropFremd && fremdAblageHinweis ? fremdAblageHinweis(Array.from(e.dataTransfer?.types ?? [])) : null;
  const dateiAblageProps = onDropFiles || onDropFremd
    ? {
        onDragOver: (e: React.DragEvent) => {
          // Eine Ordnerzeile, die einen internen Zug annimmt, hat ihn schon.
          if (e.defaultPrevented) return;
          const fremd = fremdText(e);
          if (!fremd && !(onDropFiles && ziehtDateien(Array.from(e.dataTransfer?.types ?? [])))) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
          if (!dateiAblage) setDateiAblage(true);
          if (fremdHinweis !== fremd) setFremdHinweis(fremd);
        },
        onDragLeave: (e: React.DragEvent) => {
          const nach = e.relatedTarget as Node | null;
          if (nach && (e.currentTarget as HTMLElement).contains(nach)) return;
          setDateiAblage(false);
          setFremdHinweis(null);
        },
        onDrop: (e: React.DragEvent) => {
          setDateiAblage(false);
          setFremdHinweis(null);
          if (e.defaultPrevented) return;
          if (onDropFremd && fremdText(e)) {
            e.preventDefault();
            onDropFremd(e.dataTransfer);
            return;
          }
          if (!onDropFiles || !ziehtDateien(Array.from(e.dataTransfer?.types ?? []))) return;
          e.preventDefault();
          // Synchron einsammeln — nach dem ersten await sind die Einträge weg.
          const ziel = path;
          void dateienAusDrop(e.dataTransfer)
            .then((dateien) => {
              if (dateien.length) onDropFiles(dateien, ziel);
            })
            .catch(() => undefined);
        },
      }
    : {};

  /** Drop-Props für Baum und Brotkrumen. */
  const dropZiel = onMove ? { nimmtAn, fallenLassen } : undefined;

  // Tastatur: Enter öffnet, Backspace geht eine Ebene hoch, Strg+A markiert
  // alles. Der Listener hängt am Container-Div (tabIndex), NICHT an window —
  // sonst würde er in jedem Eingabefeld der Seite feuern.
  const onKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    // Auch Schaltflächen ausnehmen: Baum, Breadcrumb, Modus-Umschalter und
    // „Hochladen" liegen alle IN diesem Container. Ohne den Guard würde Enter
    // auf einem fokussierten Knopf per preventDefault den Knopf-Klick
    // unterdrücken und stattdessen die markierte Datei öffnen.
    if (target.closest('input,textarea,select,button,a[href],[role="button"],[contenteditable]')) return;
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
      e.preventDefault();
      sel.selectAll();
      return;
    }
    if (e.key === "Escape") {
      sel.clear();
      return;
    }
    // Entf löscht die Auswahl — wie im Windows-Explorer, mit derselben
    // Rückfrage wie das Menü.
    if (e.key === "Delete" && loeschbarAnzahl > 0) {
      e.preventDefault();
      auswahlLoeschen(loeschbareAuswahl);
      return;
    }
    if (e.key === "Backspace" && browsing && path) {
      e.preventDefault();
      onNavigate(parentPath(path));
      return;
    }
    if (e.key === "Enter") {
      const first = selectableRows.find((r) => sel.isSelected(rowKeyOf(r)));
      if (first) {
        e.preventDefault();
        activateRow(first);
      }
    }
  };

  const allSelected = orderedKeys.length > 0 && orderedKeys.every((k) => sel.isSelected(k));

  const columns: Column<ExplorerRow<P>>[] = React.useMemo(() => {
    const cols: Column<ExplorerRow<P>>[] = [
      {
        id: "check",
        className: "colcheck",
        pinned: true,
        header: (
          <Checkbox
            checked={allSelected}
            onChange={() => (allSelected ? sel.clear() : sel.selectAll())}
            ariaLabel="Alle auswählen"
          />
        ),
        cell: (row) =>
          row.kind === "group" ? (
            <span aria-hidden />
          ) : (
            <Checkbox
              checked={sel.isSelected(rowKeyOf(row))}
              onChange={() => sel.toggle(rowKeyOf(row))}
              ariaLabel={`${row.kind === "folder" ? "Ordner" : "Datei"} ${row.name} auswählen`}
            />
          ),
      },
      {
        id: "name",
        header: "Name",
        className: "name",
        minWidth: 240,
        cell: (row) =>
          row.kind === "group" ? (
            <span className="docx-name docx-group-name" data-testid={`document-group-${row.key}`}>
              <FolderIcon />
              <span className="docx-name-txt">
                <span className="docx-name-base" title={row.name}>
                  {row.name}
                </span>
              </span>
            </span>
          ) : row.kind === "folder" ? (
            <span className="docx-name">
              <FolderIcon />
              <span className="docx-name-txt">
                <span className="docx-name-base" title={row.name}>
                  {row.name}
                </span>
              </span>
            </span>
          ) : (
            <span className="docx-name">
              <FileIcon fileName={row.name} mimeType={row.mimeType} />
              <span className="docx-name-txt">
                <span className="docx-name-base" title={row.name}>
                  {row.name}
                  {row.tag && onTagClick ? (
                    <button
                      type="button"
                      className="docx-tag"
                      title={row.tagTitle ?? undefined}
                      onClick={(e) => {
                        // Sonst öffnete der Zeilen-Klick zugleich die Datei.
                        e.stopPropagation();
                        onTagClick(row);
                      }}
                    >
                      {row.tag}
                    </button>
                  ) : row.tag ? (
                    <span className="docx-tree-count" title={row.tagTitle ?? undefined}>
                      {" "}
                      · {row.tag}
                    </span>
                  ) : null}
                </span>
                {/* Beim Ordner-Browsing liegen alle Dateien auf dieser Ebene —
                    die graue Pfadzeile ist dann redundant. */}
                {!browsing && row.dir ? (
                  <span className="docx-name-sub" title={row.dir}>
                    {row.dir}
                  </span>
                ) : null}
                {row.snippet ? (
                  <span className="docx-name-sub">{renderSnippet(row.snippet)}</span>
                ) : null}
              </span>
            </span>
          ),
      },
    ];

    if (showCategory) {
      cols.push({
        id: "category",
        header: "Kategorie",
        width: 150,
        cell: (row) =>
          row.kind !== "file" ? <span className="muted">—</span> : (
            <span className="muted">{row.category || "Sonstige"}</span>
          ),
      });
    }

    cols.push(
      {
        id: "type",
        header: "Typ",
        width: 120,
        cell: (row) =>
          row.kind === "group" ? "Dokumentgruppe" : row.kind === "folder" ? (
            "Dateiordner"
          ) : (
            <span title={row.mimeType || undefined}>{fileTypeLabel(row.name, row.mimeType)}</span>
          ),
      },
      {
        id: "size",
        header: "Größe",
        width: 110,
        align: "right",
        cell: (row) =>
          row.kind !== "file" ? (
            <span className="muted" title="Anzahl Dateien inkl. Unterordner">
              {row.count != null ? `${row.count} Objekte` : "—"}
            </span>
          ) : (
            humanSize(row.size)
          ),
      },
      {
        id: "date",
        header: "Geändert",
        width: 120,
        cell: (row) => (row.kind !== "file" ? <span className="muted">—</span> : fmtDate(row.date)),
      },
    );

    // Aktions-Spalte ganz rechts: öffnet dasselbe Menü wie der Rechtsklick.
    // Ohne sie wären Umbenennen und Herunterladen auf Touch-Geräten
    // unerreichbar — ein Finger löst kein `contextmenu` aus. Entfällt in der
    // read-only-Ansicht, weil es dort nichts zu tun gibt.
    if (onOpenFile || onDownload || onRename || onDelete) {
      cols.push({
        id: "act",
        className: "colact",
        header: "",
        width: 46,
        align: "center",
        pinned: true,
        cell: (row) =>
          row.kind === "file" ? (
            <button
              type="button"
              className="docx-kebab"
              aria-label={`Aktionen für ${row.name}`}
              onClick={(e) => {
                e.stopPropagation();
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                sel.handleContextMenu(rowKeyOf(row));
                setMenu({ x: r.left, y: r.bottom + 2, file: row });
              }}
            >
              ⋯
            </button>
          ) : null,
      });
    }

    return cols;
  }, [allSelected, sel, browsing, showCategory, onOpenFile, onDownload, onRename, onDelete, onTagClick]);

  const menuItems: RdMenuItem[] = React.useMemo(() => {
    if (!menu) return [];
    const file = menu.file;
    const multi = selectedFiles.length > 1;
    const items: RdMenuItem[] = [];
    if (onOpenFile) {
      items.push({ id: "open", label: "Öffnen", icon: "▸", onSelect: () => onOpenFile(file) });
    }
    if (onDownload) {
      items.push({
        id: "download",
        label: multi ? `${selectedFiles.length} Dateien herunterladen` : "Herunterladen",
        icon: "⤓",
        onSelect: () => {
          if (multi && onBulkDownload) onBulkDownload(selectedFiles);
          else onDownload(file);
        },
      });
    }
    if (onRename) {
      items.push({
        id: "rename",
        label: "Umbenennen",
        icon: "✎",
        disabled: multi,
        onSelect: () => onRename(file),
      });
    }
    if (onMoveRequest) {
      const kandidaten = (multi ? selectedFiles : [file]).filter((f) => canDrag?.(f) ?? true);
      if (kandidaten.length > 0) {
        items.push({
          id: "move",
          label: kandidaten.length > 1 ? `${kandidaten.length} Dateien verschieben nach…` : "Verschieben nach…",
          icon: "➜",
          onSelect: () => onMoveRequest(kandidaten),
        });
      }
    }
    if (onSetCategory) {
      items.push({
        id: "category",
        label: "Kategorie zuordnen…",
        icon: "▦",
        disabled: multi,
        onSelect: () => onSetCategory(file),
      });
    }
    if (items.length) items.push({ kind: "sep" });
    items.push({
      id: "copy-path",
      label: "Pfad kopieren",
      icon: "⧉",
      onSelect: () => {
        const full = [file.dir, file.name].filter(Boolean).join("/");
        void navigator.clipboard?.writeText(full);
      },
    });
    // Papierkorb: „Wiederherstellen" steht nur an einer bereits gelöschten
    // Zeile — sonst böte das Menü an jeder normalen Datei eine Aktion an, die
    // nichts täte.
    if (onRestore && file.deleted) {
      items.push({ kind: "sep" });
      items.push({
        id: "restore",
        label: "Wiederherstellen",
        icon: "↺",
        disabled: multi,
        onSelect: () => onRestore(file),
      });
    }
    // Löschen steht bewusst ganz unten hinter einer Haarlinie und rot: es ist
    // der einzige Punkt im Menü, der Daten in den Papierkorb legt, und ein
    // Fehlgriff direkt neben „Umbenennen" wäre unnötig.
    if (onDelete && !file.deleted) {
      // Mehrfach heißt hier: alles Markierte, Ordner eingeschlossen. Bisher
      // zählten nur Dateien — mit einer Datei und Ordnern in der Auswahl
      // verschwand allein die Datei.
      const mehrfachLoeschen = loeschbarAnzahl > 1;
      items.push({ kind: "sep" });
      items.push({
        id: "delete",
        label: mehrfachLoeschen ? loeschLabel(loeschbareAuswahl) : "Löschen",
        icon: "🗑",
        gefahr: true,
        disabled: mehrfachLoeschen && !onBulkDelete,
        onSelect: () => {
          if (mehrfachLoeschen) auswahlLoeschen(loeschbareAuswahl);
          else onDelete(file);
        },
      });
    }
    return items;
  }, [
    loeschbareAuswahl,
    loeschbarAnzahl,
    menu,
    selectedFiles,
    onOpenFile,
    onDownload,
    onRename,
    onMoveRequest,
    canDrag,
    onSetCategory,
    onBulkDownload,
    onDelete,
    onBulkDelete,
    onRestore,
  ]);

  const showTree = browsing && !narrow && !!treeChildren;
  // Der Splitter erscheint nur, wenn die Breite auch kontrolliert wird — sonst
  // zeigte er einen Zieh-Cursor für eine Geste, die nichts bewirkt.
  const showSplitter = showTree && !!onTreeWidthChange;
  const closeMenu = React.useCallback(() => setMenu(null), []);
  const closeFremdMenu = React.useCallback(() => setFremdMenu(null), []);

  // Splitter: Pointer-Drag nach dem Muster des Spaltenbreiten-Griffs der
  // DataTable (setPointerCapture, damit die Geste beim schnellen Ziehen nicht
  // abreißt).
  const dragRef = React.useRef<{ startX: number; startWidth: number } | null>(null);
  const [dragging, setDragging] = React.useState(false);
  const onSplitDown = (e: React.PointerEvent) => {
    if (!onTreeWidthChange) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: treeWidth };
    setDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onSplitMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || !onTreeWidthChange) return;
    onTreeWidthChange(Math.max(150, Math.min(520, Math.round(d.startWidth + (e.clientX - d.startX)))));
  };
  const onSplitUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    dragRef.current = null;
    setDragging(false);
  };

  return (
    <div
      ref={rootRef}
      className={"kvp-rd docx-root" + (narrow ? " docx-narrow" : "")}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      {toolbar ? <div className="docx-bar">{toolbar}</div> : null}
      {browsing ? (
        <ExplorerBreadcrumb
          rootLabel={rootLabel}
          path={path}
          onNavigate={onNavigate}
          segmentLabel={segmentLabel}
          dropZiel={dropZiel}
        />
      ) : null}

      <div className="docx-shell">
        {showTree ? (
          <>
            <div className="docx-tree" style={{ width: treeWidth }}>
              <FolderTree
                rootLabel={rootLabel}
                currentPath={path}
                onNavigate={onNavigate}
                childrenOf={treeChildren!}
                expandedPaths={expandedPaths ?? new Set()}
                onToggleExpand={onToggleExpand ?? (() => undefined)}
                dropZiel={dropZiel}
                onFolderContextMenu={folderMenuItems || backgroundMenuItems ? baumKontextmenue : undefined}
                ziehen={onMove ? { darf: (f) => (canDrag ? canDrag(f) : true), starten: baumZugStarten } : undefined}
              />
            </div>
            {showSplitter ? (
              <div
                className={"docx-split" + (dragging ? " on" : "")}
                role="separator"
                aria-orientation="vertical"
                aria-label="Breite des Ordnerbaums ändern"
                onPointerDown={onSplitDown}
                onPointerMove={onSplitMove}
                onPointerUp={onSplitUp}
                onPointerCancel={onSplitUp}
              />
            ) : null}
          </>
        ) : null}

        <div
          className={"docx-main" + (dateiAblage ? " docx-ablage-aktiv" : "")}
          onContextMenu={onBackgroundContextMenu}
          style={onDropFiles || onDropFremd ? { position: "relative" } : undefined}
          {...dateiAblageProps}
        >
          {dateiAblage ? (
            <div className="docx-ablage" role="status">
              <b>Hier ablegen</b>
              <span>{fremdHinweis ?? dropFilesHinweis ?? "Dateien hochladen"}</span>
            </div>
          ) : null}
          {/* Eigener Ladezustand: die DataTable rendert ihren `emptyText` nur,
              wenn NICHT geladen wird — ohne das hier bliebe die Fläche beim
              Ordnerwechsel kommentarlos leer. */}
          {loading && rows.length === 0 ? <div className="docx-empty">Lade …</div> : null}
          <DataTable<ExplorerRow<P>>
            columns={columns}
            rows={rows}
            rowKey={(row) => rowKeyOf(row)}
            tableClassName="docx-tbl"
            card={false}
            onRowClick={handleRowClick}
            onRowContextMenu={handleRowContextMenu}
            rowDraggable={onMove || onDragOut ? (row) => greifbar(row) : undefined}
            onRowDragStart={onMove || onDragOut ? (row, _i, e) => zugStarten(row, e) : undefined}
            rowDropTarget={onMove ? (row) => row.kind === "folder" && nimmtAn(row.path) : undefined}
            onRowDrop={onMove ? (row) => row.kind === "folder" && fallenLassen(row.path) : undefined}
            belowTable={belowList}
            kopfZeilen={kopfZeilen}
            rowSelected={(row) => sel.isSelected(rowKeyOf(row))}
            rowChildren={(row) => (row.kind === "group" ? row.files : [])}
            expandedKeys={expandedGroupKeys}
            rowClassName={(row) => {
              const classes: string[] = [];
              if (row.kind === "group") classes.push("docx-group-row");
              if (row.kind === "file") {
                if (row.dimmed) classes.push("docx-dim");
                if (!onOpenFile) classes.push("docx-noclick");
              }
              return classes.join(" ") || undefined;
            }}
            // Ordner sind immer klickbar; Dateien nur, wenn es etwas zu öffnen
            // gibt. In der read-only-Ansicht markiert der Klick lediglich —
            // ein Zeigefinger-Cursor würde dort mehr versprechen.
            rowClickable={(row) => row.kind === "folder" || (row.kind === "file" && !!onOpenFile)}
            emptyText={emptyText ?? "Dieser Ordner ist leer."}
            loading={loading}
            hasMore={hasMore}
            // onLoadMore MUSS gesetzt sein: nur in diesem Zweig rendert die
            // DataTable ihren eigenen `.tablewrap`-Scroll-Container — den
            // braucht der Sticky-Header und der InfiniteScroll.
            onLoadMore={onLoadMore ?? (() => undefined)}
            scrollContainerId={scrollContainerId}
          />
        </div>
      </div>

      <RdContextMenu
        position={menu ? { x: menu.x, y: menu.y } : null}
        items={menuItems}
        onClose={closeMenu}
        title={menu ? menu.file.name : undefined}
      />
      <RdContextMenu
        position={fremdMenu ? { x: fremdMenu.x, y: fremdMenu.y } : null}
        items={fremdMenu?.items ?? []}
        onClose={closeFremdMenu}
        title={fremdMenu?.title}
      />
    </div>
  );
}
